import React, { useEffect, useRef, useState } from "react";
import { AppShell } from "../../components/Sidebar";
import { api, API } from "../../lib/api";
import { UploadCloud, FileSpreadsheet, Download, CheckCircle2, AlertTriangle, XCircle, Copy } from "lucide-react";
import { toast } from "sonner";

const TEMPLATE = `roll_no,first_name,last_name,date_of_birth,grade,division,email,mobile
11,Riya,Sharma,2509,8,B,riya@example.com,+91 9123456789
12,Aakash,Iyer,1812,8,B,,
`;

export default function BulkImport() {
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const [grade, setGrade] = useState("8");
  const [division, setDivision] = useState("B");
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    api.get("/admin/schools").then((r) => {
      setSchools(r.data);
      setSchoolId(r.data[0]?.id || "");
    });
  }, []);

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "students_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a CSV file");
    setSubmitting(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = new URLSearchParams({ school_id: schoolId, grade, division });
      const token = localStorage.getItem("gd_token");
      const res = await fetch(`${API}/admin/students/bulk-import?${params}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Upload failed");
      setResult(data);
      toast.success(`Created ${data.summary.created}, skipped ${data.summary.skipped}, errors ${data.summary.errors}`);
    } catch (err) {
      toast.error(err.message);
    } finally { setSubmitting(false); }
  };

  const copyAll = () => {
    if (!result) return;
    const text = result.created.map((c) => `${c.name}\t${c.username}\t${c.password}\t${c.parent_username}\t${c.parent_password}`).join("\n");
    navigator.clipboard.writeText(`Name\tStudent Username\tStudent Password\tParent Username\tParent Password\n${text}`);
    toast.success("Copied credentials to clipboard");
  };

  return (
    <AppShell role="admin">
      <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Bulk Import Students</h1>
      <p className="mt-1 text-slate-500">Upload a CSV file to onboard a class in seconds. Email and mobile are optional.</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_300px]">
        <form onSubmit={submit} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-card">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="School">
              <select required data-testid="import-school" value={schoolId} onChange={(e) => setSchoolId(e.target.value)} className={inp}>
                {schools.map((sc) => <option key={sc.id} value={sc.id}>{`${sc.name} (${sc.code})`}</option>)}
              </select>
            </Field>
            <Field label="Default Grade"><input required data-testid="import-grade" value={grade} onChange={(e) => setGrade(e.target.value)} className={inp} /></Field>
            <Field label="Default Division"><input required data-testid="import-division" value={division} onChange={(e) => setDivision(e.target.value.toUpperCase())} className={inp} /></Field>
          </div>

          <div className="mt-5">
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) setFile(f); }}
              onClick={() => fileRef.current?.click()}
              className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-indigo-200 bg-indigo-50/50 p-10 text-center transition hover:bg-indigo-50"
            >
              <UploadCloud size={36} className="text-indigo-500" />
              <p className="mt-2 text-sm font-semibold text-slate-700">{file ? file.name : "Drag & drop a CSV file, or click to browse"}</p>
              <p className="text-xs text-slate-500">Required columns: roll_no, first_name, last_name, date_of_birth (DDMM)</p>
              <input ref={fileRef} data-testid="import-file" type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-3">
            <button type="button" onClick={downloadTemplate} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <Download size={14} /> Download CSV Template
            </button>
            <button data-testid="import-submit" disabled={submitting || !file} className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700 disabled:opacity-60">
              <UploadCloud size={14} /> {submitting ? "Uploading…" : "Upload & Create"}
            </button>
          </div>
        </form>

        <aside className="rounded-2xl border border-slate-100 bg-white p-5 shadow-card">
          <div className="flex items-center gap-2"><FileSpreadsheet size={18} className="text-indigo-500" /><h3 className="text-sm font-bold text-slate-900">CSV format</h3></div>
          <p className="mt-2 text-xs text-slate-500">First row is a header. Date of Birth must be 4 digits in DDMM (e.g. 1408 for 14-Aug). Email & Mobile are optional.</p>
          <p className="mt-3 text-xs text-slate-500">Username = SchoolCode + Class + Division + FirstName + LastInitial<br />Password = firstname + DDMM<br />Parent login is auto-generated.</p>
        </aside>
      </div>

      {result && (
        <div className="mt-7 rounded-2xl border border-slate-100 bg-white p-6 shadow-card" data-testid="import-result">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-lg font-bold text-slate-900">Import Summary</h3>
            <div className="flex gap-2 text-xs">
              <Pill icon={CheckCircle2} color="bg-emerald-50 text-emerald-700">Created: {result.summary.created}</Pill>
              <Pill icon={AlertTriangle} color="bg-amber-50 text-amber-700">Skipped: {result.summary.skipped}</Pill>
              <Pill icon={XCircle} color="bg-rose-50 text-rose-700">Errors: {result.summary.errors}</Pill>
            </div>
          </div>

          {result.created.length > 0 && (
            <>
              <div className="mt-5 flex items-center justify-between">
                <h4 className="text-sm font-bold text-slate-700">Created accounts</h4>
                <button onClick={copyAll} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                  <Copy size={12} /> Copy all
                </button>
              </div>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full min-w-[800px] text-left text-xs">
                  <thead>
                    <tr className="border-b border-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-4">Roll</th>
                      <th className="py-2 pr-4">Name</th>
                      <th className="py-2 pr-4">Student Username</th>
                      <th className="py-2 pr-4">Student Password</th>
                      <th className="py-2 pr-4">Parent Username</th>
                      <th className="py-2 pr-4">Parent Password</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {result.created.map((c, i) => (
                      <tr key={i} className="border-b border-slate-50">
                        <td className="py-2 pr-4">{c.roll_no}</td>
                        <td className="py-2 pr-4 font-sans font-medium text-slate-900">{c.name}</td>
                        <td className="py-2 pr-4">{c.username}</td>
                        <td className="py-2 pr-4">{c.password}</td>
                        <td className="py-2 pr-4">{c.parent_username}</td>
                        <td className="py-2 pr-4">{c.parent_password}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {(result.skipped.length > 0 || result.errors.length > 0) && (
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {result.skipped.length > 0 && (
                <ListBox title="Skipped" items={result.skipped.map((s) => `Row ${s.row}: ${s.reason} (${s.username})`)} color="amber" />
              )}
              {result.errors.length > 0 && (
                <ListBox title="Errors" items={result.errors.map((e) => `Row ${e.row}: ${e.error}`)} color="rose" />
              )}
            </div>
          )}
        </div>
      )}
    </AppShell>
  );
}

const inp = "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100";
const Field = ({ label, children }) => (<label className="block"><span className="mb-1.5 block text-sm font-medium text-slate-700">{label}</span>{children}</label>);

const Pill = ({ icon: Icon, color, children }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${color}`}><Icon size={12} /> {children}</span>
);

const ListBox = ({ title, items, color }) => {
  const c = color === "amber" ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-rose-50 border-rose-200 text-rose-800";
  return (
    <div className={`rounded-xl border p-4 text-sm ${c}`}>
      <h5 className="font-bold">{title}</h5>
      <ul className="mt-2 list-disc space-y-1 pl-4 text-xs">
        {items.map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    </div>
  );
};
